import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurrentStudyProvider } from "@/context/CurrentStudyContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import UploadPage from "@/pages/UploadPage";
import HistoryPage from "@/pages/HistoryPage";
import LatestStudyPage from "@/pages/LatestStudyPage";
import StalledStockPage from "@/pages/StalledStockPage";
import DispensationDetailPage from "@/pages/DispensationDetailPage";
import ComparisonPage from "@/pages/ComparisonPage";
import IncentivesPage from "@/pages/IncentivesPage";
import SimulatorPage from "@/pages/SimulatorPage";
import CatalogPage from "@/pages/CatalogPage";
import UsersPage from "@/pages/UsersPage";
import LicensesPage from "@/pages/LicensesPage";
import AdminErrorsPage from "@/pages/AdminErrorsPage";
import ConsultancyImprovementsPage from "@/pages/ConsultancyImprovementsPage";
import LeadsPage from "@/pages/LeadsPage";
import ServicesPage from "@/pages/ServicesPage";
import DownloadsPage from "@/pages/DownloadsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import EmployeesPage from "@/pages/EmployeesPage";
import EmployeeAnalysisPage from "@/pages/EmployeeAnalysisPage";
import BenchmarkPage from "@/pages/BenchmarkPage";
import AccountPage from "@/pages/AccountPage";
import StockPage from "@/pages/StockPage";
import MinMaxPage from "@/pages/MinMaxPage";
import RecommendationsPage from "@/pages/RecommendationsPage";
import OrdersPage from "@/pages/OrdersPage";
import PreferredLabsPage from "@/pages/PreferredLabsPage";
import TrasladoSimplePage from "@/pages/TrasladoSimplePage";
import ProveedoresHubPage from "@/pages/ProveedoresHubPage";
import SupplierConditionsHubPage from "@/pages/SupplierConditionsHubPage";
import CuadroMandoPage from "@/pages/CuadroMandoPage";
import StudySummaryPage from "@/pages/StudySummaryPage";
import LandingPage from "@/pages/LandingPage";
import AdminCampaignPage from "@/pages/AdminCampaignPage";
import ReviewFormPage from "@/pages/ReviewFormPage";
import MoleculesNoStockPage from "@/pages/MoleculesNoStockPage";
import AppLayout from "@/components/AppLayout";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500" data-testid="auth-loading">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Marca blanca: las licencias Sandoz no tienen comparativa ni condiciones de laboratorio
function NoBrandBlock({ children }) {
  const { user } = useAuth();
  if (user?.brand === "sandoz") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors toastOptions={{ style: { maxWidth: "calc(100vw - 32px)" } }} />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/informacion" element={<LandingPage />} />
          <Route path="/farmaclass" element={<LandingPage />} />
          <Route path="/resena/:token" element={<ReviewFormPage />} />
          <Route
            path="/"
            element={
              <Protected>
                <CurrentStudyProvider>
                  <AppLayout />
                </CurrentStudyProvider>
              </Protected>
            }
          >
            <Route index element={<StudySummaryPage />} />
            <Route path="resumen/:id" element={<StudySummaryPage />} />
            <Route path="dashboard-legacy" element={<DashboardPage />} />
            <Route path="upload" element={<UploadPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="latest-study" element={<LatestStudyPage />} />
            <Route path="solo-marca" element={<LatestStudyPage tab="molecules" state="solo_marca" sort="marca_euros" dir="desc" />} />
            <Route path="stock-parado" element={<StalledStockPage />} />
            <Route path="comparison" element={<NoBrandBlock><ComparisonPage /></NoBrandBlock>} />
            <Route path="incentives" element={<IncentivesPage />} />
            <Route path="simulator" element={<SimulatorPage />} />
            <Route path="benchmark" element={<BenchmarkPage />} />
            <Route path="dispensations/:id" element={<DispensationDetailPage />} />
            <Route path="catalog" element={<CatalogPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="licenses" element={<LicensesPage />} />
            <Route path="admin/errors" element={<AdminErrorsPage />} />
            <Route path="admin/mejoras" element={<ConsultancyImprovementsPage />} />
            <Route path="admin/correo-clientes" element={<AdminCampaignPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="empleados" element={<EmployeesPage />} />
            <Route path="empleados/analisis" element={<EmployeeAnalysisPage />} />
            <Route path="cuadro-mando" element={<CuadroMandoPage />} />
            <Route path="sin-stock-genericos" element={<MoleculesNoStockPage />} />
            <Route path="stock" element={<StockPage />} />
            <Route path="minimos-maximos" element={<MinMaxPage />} />
            <Route path="recomendaciones" element={<RecommendationsPage />} />
            <Route path="proveedores" element={<ProveedoresHubPage />} />
            <Route path="proveedores/condiciones" element={<NoBrandBlock><SupplierConditionsHubPage /></NoBrandBlock>} />
            <Route path="pedido/falta" element={<OrdersPage mode="missing" />} />
            <Route path="pedido/reposicion" element={<OrdersPage mode="replenishment" />} />
            <Route path="proveedores/preferentes" element={<PreferredLabsPage />} />
            <Route path="pedido/traslado-simple" element={<TrasladoSimplePage />} />
            <Route path="servicios" element={<ServicesPage />} />
            <Route path="descargas" element={<DownloadsPage />} />
            <Route path="payment-success" element={<PaymentSuccessPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
